// src/components/atomic-crm/deals/useDealImport.tsx
import { useDataProvider, useGetIdentity } from "ra-core";
import { useCallback } from "react";
import { supabase } from "../providers/supabase/supabase";

export type DealImportSchema = {
  name: string;
  amount: string;
  stage: string;
  description: string;
  company_name: string;
  expected_closing_date?: string;
};

export function useDealImport() {
  const dataProvider = useDataProvider();
  const user = useGetIdentity();

  const processBatch = useCallback(
    async (batch: DealImportSchema[]) => {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) throw new Error("Not authenticated");

      const { data: sale } = await supabase
        .from("sales")
        .select("org_id")
        .eq("user_id", authUser.id)
        .maybeSingle();

      const orgId = sale?.org_id;
      if (!orgId) throw new Error("No org_id found");

      // Fetch companies to map names to IDs
      const { data: companies } = await supabase
        .from("companies")
        .select("id, name")
        .eq("org_id", orgId);

      const companyMap = new Map(
        companies?.map((c) => [c.name.trim().toLowerCase(), c.id]) || [],
      );

      await Promise.all(
        batch.map(async (row) => {
          if (!row.name?.trim()) return;

          const companyKey = row.company_name?.trim().toLowerCase();
          const companyId = companyKey ? companyMap.get(companyKey) : undefined;

          await dataProvider.create("deals", {
            data: {
              name: row.name.trim(),
              amount: row.amount ? parseFloat(row.amount) : 0,
              stage: row.stage || "opportunity",
              description: row.description,
              expected_closing_date: row.expected_closing_date
                ? new Date(row.expected_closing_date).toISOString()
                : null,
              company_id: companyId,
              sales_id: user?.identity?.id,
              org_id: orgId,
            },
          });
        }),
      );
    },
    [dataProvider, user?.identity?.id],
  );

  return processBatch;
}
